import { positionStatus } from "./PositionDial";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Mesh, MeshBasicMaterial } from "three";
import { disposeObject } from "./threeResources";
import type { CameraView } from "./camera";
import type { OrientationModelConfig } from "./config";
import type { ModelAppearance } from "./preferences";
import {
  quaternionEulerDegrees,
  quaternionToXyzw,
  tiltOnlyQuaternion,
  type OrientationSelection,
} from "./orientation";

type SceneState = {
  root: { visible: boolean; quaternion: { set(x: number, y: number, z: number, w: number): void } };
  applyCameraView(cameraView: CameraView): void;
  setAppearance(appearance: ModelAppearance): void;
  setGravityArrowColor(color: string): void;
  setGravityDirectionVisible(visible: boolean): void;
  render(): void;
  dispose(): void;
};

export function shouldShowGravityLegend(showDirection: boolean, showLegend: boolean, text: string): boolean {
  return showDirection && showLegend && text.length > 0;
}

export function OrientationPanel(props: {
  showExplanation?: boolean;
  appearance: ModelAppearance;
  cameraView: CameraView;
  config: OrientationModelConfig;
  gravityArrowColor: string;
  gravityLegendText: string;
  onCameraViewChange(cameraView: CameraView): void;
  selection: OrientationSelection | null;
  showGravityDirection: boolean;
  showGravityLegend: boolean;
  suppressRelativeHeading: boolean;
  verticalPositionThresholdDegrees?: number;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const scene = useRef<SceneState | null>(null);
  const latest = useRef(props.selection);
  const latestCameraView = useRef(props.cameraView);
  const latestAppearance = useRef(props.appearance);
  const latestGravityArrowColor = useRef(props.gravityArrowColor);
  const latestGravityDirection = useRef(props.showGravityDirection);
  const latestSuppressRelativeHeading = useRef(props.suppressRelativeHeading);
  const onCameraViewChange = useRef(props.onCameraViewChange);
  const [renderError, setRenderError] = useState(false);
  latest.current = props.selection;
  latestCameraView.current = props.cameraView;
  latestAppearance.current = props.appearance;
  latestGravityArrowColor.current = props.gravityArrowColor;
  latestGravityDirection.current = props.showGravityDirection;
  latestSuppressRelativeHeading.current = props.suppressRelativeHeading;
  onCameraViewChange.current = props.onCameraViewChange;

  const display = useMemo(() => {
    if (props.selection?.state !== "available") return null;
    const tilt = tiltOnlyQuaternion(props.selection.model);
    return {
      quaternion: props.suppressRelativeHeading ? tilt.quaternion : props.selection.model,
      inclinationDegrees: tilt.inclinationDegrees,
      singular: props.suppressRelativeHeading && tilt.singular,
    };
  }, [props.selection, props.suppressRelativeHeading]);

  // Dispose controls before React detaches their canvas/root event target.
  useLayoutEffect(() => {
    let disposed = false;
    let release: (() => void) | undefined;
    if (!canvas.current) return;
    async function initialize() {
      try {
        const THREE = await import("three");
        const { GLTFLoader } = await import("three/examples/jsm/loaders/GLTFLoader.js");
        const { OrbitControls } = await import("three/examples/jsm/controls/OrbitControls.js");
        if (disposed || !canvas.current) return;
        const renderer = new THREE.WebGLRenderer({ canvas: canvas.current, alpha: true, antialias: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        const sceneWidth = 320;
        const sceneHeight = 220;
        renderer.setSize(sceneWidth, sceneHeight, false);
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        const world = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(34, sceneWidth / sceneHeight, 0.01, 20);
        // Establish ownership before awaiting the asset; teardown also covers
        // partial initialization and late/failed loads during mode changes.
        release = () => { disposeObject(world); renderer.dispose(); renderer.forceContextLoss(); };
        const controls = new OrbitControls(camera, canvas.current);
        release = () => { controls.dispose(); disposeObject(world); renderer.dispose(); renderer.forceContextLoss(); };
        controls.enableDamping = false;
        controls.enablePan = false;
        controls.enableRotate = true;
        controls.enableZoom = true;
        controls.minDistance = 1.4;
        controls.maxDistance = 3.2;
        controls.minPolarAngle = 0.12;
        controls.maxPolarAngle = Math.PI - 0.12;
        let applyingCameraView = false;
        let interacting = false;
        const readCameraView = (): CameraView => ({
          position: [camera.position.x, camera.position.y, camera.position.z],
          target: [controls.target.x, controls.target.y, controls.target.z],
        });
        const applyCameraView = (cameraView: CameraView) => {
          applyingCameraView = true;
          camera.position.set(...cameraView.position);
          controls.target.set(...cameraView.target);
          controls.update();
          applyingCameraView = false;
          renderer.render(world, camera);
        };
        controls.addEventListener("start", () => { interacting = true; });
        controls.addEventListener("end", () => { interacting = false; });
        controls.addEventListener("change", () => {
          renderer.render(world, camera);
          if (interacting && !applyingCameraView) onCameraViewChange.current(readCameraView());
        });
        applyCameraView(latestCameraView.current);
        const root = new THREE.Group();
        const alignment = new THREE.Group();
        alignment.rotation.x = THREE.MathUtils.degToRad(props.config.meshAlignmentXDegrees);
        root.add(alignment);
        world.add(root);
        const worldDown = new THREE.ArrowHelper(
          new THREE.Vector3(0, -1, 0),
          new THREE.Vector3(-0.72, 0.72, 0),
          0.72,
          new THREE.Color(latestGravityArrowColor.current),
          0.14,
          0.08,
        );
        worldDown.visible = latestGravityDirection.current;
        world.add(worldDown);
        const gltf = await new GLTFLoader().loadAsync(props.config.assetUrl);
        if (disposed) { disposeObject(gltf.scene); return; }
        alignment.add(gltf.scene);
        const materials: MeshBasicMaterial[] = [];
        const model = gltf.scene;
        model.traverse((item) => {
          const mesh = item as Mesh;
          if (!mesh.isMesh || !mesh.geometry) return;
          const original = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
          original.forEach((material) => material.dispose());
          const material = new THREE.MeshBasicMaterial({ transparent: true });
          materials.push(material);
          mesh.material = material;
          const edges = new THREE.LineSegments(
            new THREE.EdgesGeometry(mesh.geometry, 28),
            new THREE.LineBasicMaterial({ color: 0x9affec, transparent: true, opacity: 0.72 }),
          );
          mesh.add(edges);
        });
        const bounds = new THREE.Box3().setFromObject(model);
        const size = bounds.getSize(new THREE.Vector3());
        const scale = props.config.normalizedSize / Math.max(size.x, size.y, size.z, 1e-6);
        model.scale.setScalar(scale);
        model.position.set(-props.config.pivot[0] * scale, -props.config.pivot[1] * scale, -props.config.pivot[2] * scale);
        alignment.add(model);
        const render = () => renderer.render(world, camera);
        const setAppearance = (appearance: ModelAppearance) => {
          materials.forEach((material) => {
            material.color.set(appearance.color);
            material.opacity = appearance.opacity;
            material.needsUpdate = true;
          });
          render();
        };
        const setGravityDirectionVisible = (visible: boolean) => {
          worldDown.visible = visible;
          render();
        };
        const setGravityArrowColor = (color: string) => {
          worldDown.setColor(new THREE.Color(color));
          render();
        };
        const dispose = () => {
          release?.();
        };
        scene.current = { root, applyCameraView, setAppearance, setGravityArrowColor, setGravityDirectionVisible, render, dispose };
        setAppearance(latestAppearance.current);
        setGravityArrowColor(latestGravityArrowColor.current);
        setGravityDirectionVisible(latestGravityDirection.current);
        const selection = latest.current;
        root.visible = selection?.state === "available";
        if (selection?.state === "available") {
          const quaternion = latestSuppressRelativeHeading.current
            ? tiltOnlyQuaternion(selection.model).quaternion
            : selection.model;
          const [x, y, z, w] = quaternionToXyzw(quaternion);
          root.quaternion.set(x, y, z, w);
        }
        render();
      } catch {
        release?.();
        release = undefined;
        if (!disposed) setRenderError(true);
      }
    }
    void initialize();
    return () => {
      disposed = true;
      release?.();
      release = undefined;
      scene.current = null;
    };
  }, [props.config]);

  useEffect(() => {
    if (!scene.current) return;
    scene.current.root.visible = display != null;
    if (display) {
      const [x, y, z, w] = quaternionToXyzw(display.quaternion);
      scene.current.root.quaternion.set(x, y, z, w);
    }
    scene.current.render();
  }, [display]);

  useEffect(() => { scene.current?.applyCameraView(props.cameraView); }, [props.cameraView]);
  useEffect(() => { scene.current?.setAppearance(props.appearance); }, [props.appearance]);
  useEffect(() => { scene.current?.setGravityArrowColor(props.gravityArrowColor); }, [props.gravityArrowColor]);
  useEffect(() => { scene.current?.setGravityDirectionVisible(props.showGravityDirection); }, [props.showGravityDirection]);

  const available = display != null;
  const euler = display ? quaternionEulerDegrees(display.quaternion) : null;
  return (
    <section
      className="orientation-panel"
      data-calibration={props.config.calibrationVersion}
      data-camera-position={props.cameraView.position.join(",")}
      data-camera-target={props.cameraView.target.join(",")}
      data-pose-state={props.selection?.state || "unavailable"}
      data-sample-time={props.selection?.state === "available" ? props.selection.sampleTime : undefined}
    >
      <header><strong>{props.config.displayName}</strong><details className="orientation-info"><summary aria-label="About orientation">i</summary><span role="note">Experimental gravity-relative display. Relative heading may drift. Torso alignment is provisional.</span></details></header>
      <div className="orientation-scene">
        <canvas aria-label={`${props.config.displayName} 3D model; drag to rotate view`} ref={canvas} />
        {shouldShowGravityLegend(props.showGravityDirection, props.showGravityLegend, props.gravityLegendText) &&
          <span className="world-down-label">{props.gravityLegendText}</span>}
        {(!available || renderError) && (
          <p className="orientation-unavailable" role="status">
            {renderError ? "3D view unavailable; signals remain available" : positionStatus(props.selection, props.verticalPositionThresholdDegrees)}
          </p>
        )}
      </div>
      <footer className={props.showExplanation ? undefined : "orientation-inline-hidden"}>
        <span><small>{props.suppressRelativeHeading ? "Tilt X" : "Pitch"}</small><strong>{euler ? `${euler.pitch.toFixed(1)}°` : "—"}</strong></span>
        <span><small>{props.suppressRelativeHeading ? "Tilt Z" : "Roll"}</small><strong>{euler ? `${euler.roll.toFixed(1)}°` : "—"}</strong></span>
        <span><small>{props.suppressRelativeHeading ? "Inclination" : "Heading"}</small><strong>{display ? (props.suppressRelativeHeading ? `${display.inclinationDegrees.toFixed(1)}°` : "Relative") : "—"}</strong></span>
      </footer>
      {props.showExplanation && (display?.singular || props.config.provisional) && <p className="orientation-note">
        {[display?.singular ? "Singular upside-down pose" : null,
          props.config.provisional ? "Torso alignment provisional" : null].filter(Boolean).join(" · ")}
      </p>}
    </section>
  );
}
